import React, { useEffect, useState } from 'react';
import { APP_ENV, APP_VERSION } from '../../config/app';
import { useTenants } from '../../contexts/TenantContext';
import { useAuth, useProfile } from '../../hooks';
import { formatAppVersionLabel } from '../../utils/appRelease';
import { avatarInitial, displayUserName, roleLabel, userEmail } from '../../utils/userDisplay';
import { Avatar, Button, ModalPortal } from '../ui';
import { TenantSwitcher } from './TenantSwitcher';

interface SidebarUserProfileProps {
  collapsed?: boolean;
  onLogout: () => void;
}

const SIGNOUT_ICON = 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1';

export const SidebarUserProfile: React.FC<SidebarUserProfileProps> = ({
  collapsed = false,
  onLogout,
}) => {
  const { user, accountType } = useAuth();
  const { profile } = useProfile();
  const { tenants, currentTenantId, currentTenant, switchTenant, switching } = useTenants();
  const [showModal, setShowModal] = useState(false);

  const displayName = displayUserName(profile, user);
  const initial = avatarInitial(displayName);
  const email = userEmail(profile, user);
  const role = roleLabel(accountType);
  const showTenantDropdown = tenants.length > 1;
  const subtitle = showTenantDropdown
    ? role
    : currentTenant?.name
      ? `${role} · ${currentTenant.name}`
      : role;
  const versionLabel = formatAppVersionLabel(APP_VERSION, APP_ENV);
  const tooltip = [displayName, role].filter(Boolean).join(' · ');

  useEffect(() => {
    if (!showModal) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      setShowModal(false);
    };
    document.addEventListener('keydown', handleEscape, true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape, true);
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  const renderTenantRow = (name: string, isCurrent: boolean, tenantId?: string) => {
    const chip = isCurrent ? (
      <span className="text-[10px] font-bold uppercase tracking-wide text-brand-800 bg-brand-200/80 px-1.5 py-0.5 rounded-full">
        Current
      </span>
    ) : null;

    const inner = (
      <>
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {name}
          {chip}
        </div>
        <div className="mt-0.5 text-sm text-gray-500">{role}</div>
      </>
    );

    if (!isCurrent && tenantId) {
      return (
        <li key={tenantId}>
          <button
            type="button"
            disabled={switching}
            onClick={() => switchTenant(tenantId)}
            className="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-colors disabled:opacity-60"
          >
            {inner}
          </button>
        </li>
      );
    }

    return (
      <li
        key={tenantId ?? name}
        className={`px-3 py-2.5 rounded-lg border ${
          isCurrent ? 'border-brand-400 bg-brand-50/80' : 'border-gray-200'
        }`}
      >
        {inner}
      </li>
    );
  };

  const tenantRows = (() => {
    if (tenants.length > 0) {
      return (
        <ul className="space-y-2">
          {tenants.map(tenant =>
            renderTenantRow(tenant.name, tenant.id === currentTenantId, tenant.id),
          )}
        </ul>
      );
    }
    if (currentTenant) {
      return (
        <ul className="space-y-2">
          {renderTenantRow(currentTenant.name, true, currentTenant.id)}
        </ul>
      );
    }
    return <p className="text-sm text-gray-500 m-0">No tenants available.</p>;
  })();

  return (
    <>
      <div className={collapsed ? 'w-full' : ''}>
        <button
          type="button"
          className={`flex items-center gap-3 w-full min-w-0 rounded-lg text-left transition-colors ${
            collapsed ? 'justify-center p-1.5' : 'px-1.5 py-1.5'
          } hover:bg-white/10`}
          onClick={() => setShowModal(true)}
          title={tooltip}
          aria-label={`Open profile for ${displayName}`}
        >
          <Avatar
            src={profile?.photo_url ?? undefined}
            alt={displayName}
            size="sm"
            fallback={initial}
            className="ring-2 ring-brand-400/50 flex-shrink-0"
          />
          {!collapsed && (
            <span className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-semibold text-white truncate">{displayName}</span>
              <span className="text-[11px] text-brand-300 truncate">{subtitle}</span>
            </span>
          )}
        </button>

        {!collapsed && (
          <div className="mt-1 px-0.5">
            <TenantSwitcher />
          </div>
        )}
        {collapsed && tenants.length > 1 && <TenantSwitcher collapsed />}

        <button
          type="button"
          className={`w-full flex items-center gap-2 mt-1 text-sm font-medium text-brand-200 transition-colors ${
            collapsed
              ? 'justify-center p-2 rounded-lg hover:text-red-300 hover:bg-red-500/20'
              : 'px-1.5 py-2 border-t border-white/10 rounded-none hover:text-red-300 hover:bg-red-500/20'
          }`}
          onClick={onLogout}
          title={collapsed ? 'Sign out' : undefined}
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={SIGNOUT_ICON} />
          </svg>
          {!collapsed && <span>Sign out</span>}
        </button>

        {!collapsed && versionLabel ? (
          <div className="mt-1.5 px-1.5 text-[11px] text-white/40 truncate" title={versionLabel}>
            {versionLabel}
          </div>
        ) : null}
      </div>

      {showModal && (
        <ModalPortal>
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 print:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-modal-title"
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <h2 id="profile-modal-title" className="text-base font-bold text-gray-900">Profile</h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="flex items-center gap-3.5 mb-5">
                <Avatar
                  src={profile?.photo_url ?? undefined}
                  alt={displayName}
                  size="md"
                  fallback={initial}
                />
                <div className="min-w-0">
                  <div className="text-[1.05rem] font-semibold text-gray-900 leading-snug">{displayName}</div>
                  {email ? <div className="text-sm text-gray-500 break-all">{email}</div> : null}
                  {versionLabel ? <div className="mt-1 text-xs text-gray-400">{versionLabel}</div> : null}
                </div>
              </div>

              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2.5">Tenants</h3>
              {tenantRows}
            </div>

            <div className="flex gap-2 px-5 py-4 border-t border-gray-100">
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)} fullWidth>
                Close
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => {
                  setShowModal(false);
                  onLogout();
                }}
                fullWidth
              >
                Sign out
              </Button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </>
  );
};
